import React, { useCallback, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';

import { SEARCH_DEBOUNCE_MS } from '~/common/constants/debounces';
import { QUICK_SEARCH_DOCUMENT_COUNT } from '~/common/constants/max_lengths';
import { DEFAULT_SORT_BY } from '~/common/constants/search';
import {
  generateTextStringForFilterTags,
  toFilter,
} from '~/components/SearchV2/SearchV2.utils';
import { checkSearchQueryHasUnsupportedEntity } from '~/components/SearchV2/utils';
import { trackSegment } from '~/components/SegmentAnalytics';
import BaseBooleanSearchBar from '~/components/Shared/BaseBooleanSearchBar/BaseBooleanSearchBar';
import { Box } from '~/eds/src/components/Box/Box';
import { Button } from '~/eds/src/components/Button/Button';
import { ContentContainer } from '~/eds/src/components/ContentContainer/ContentContainer';
import { Divider } from '~/eds/src/components/Divider/Divider';
import { Layout } from '~/eds/src/components/Layout/Layout';
import { Link } from '~/eds/src/components/Link/Link';
import { Menu } from '~/eds/src/components/Menu/Menu';
import { PageOverlay } from '~/eds/src/components/PageOverlay/PageOverlay';
import { Text } from '~/eds/src/components/Text/Text';
import { QueryEntityType } from '~/enums/QueryEntityType';
import { Filters as FiltersType } from '~/evifields/src/types/Filters';
import { Option } from '~/evifields/src/types/Option';
import { BooleanSearchBar } from '~/features/advanced-search/boolean-search-bar/BooleanSearchBar';
import { ReadOnlyComplexQuery } from '~/features/advanced-search/read-only-complex-query/ReadOnlyComplexQuery';
import { SearchQuery } from '~/features/advanced-search/types';
import { buildQuery } from '~/features/advanced-search/utils/build-query';
import { FlagType } from '~/flags/flags';
import { useFlag } from '~/flags/useFlag';
import { usePinnedFilters as usePinnedFiltersDeprecated } from '~/hooks/usePinnedFilters_DEPRECATED';
import { useRecentSearches } from '~/hooks/useRecentSearches';
import { useFormatDate } from '~/i18n/hooks/useFormatDate/useFormatDate';
import { api } from '~/redux/api/createApi';
import {
  BooleanTextSearchEntity,
  Folder,
  PilotSearchQuery,
  RecentSearches,
  testIsSupportedFilterType,
  toFilters,
} from '~/redux/api/methods/search';
import { toSearchV3Filters } from '~/redux/api/methods/searchV3';
import { slices } from '~/redux/slices';
import search, { isActiveFilter } from '~/redux/slices/search';
import { useRouting } from '~/routing/useRouting';
import { debounce } from '~/utils/debounce';
import { getFileIconNameByFileType } from '~/utils/fileTypeIcon';

import EcBooleanTextSearchPopup from '../EcBooleanTextSearchPopup/EcBooleanTextSearchPopup';

type Props = {
  isVisible: boolean;
  hideModal: () => void;
  onSearchSubmit: (query: string) => void;
};

const QuickSearchModal = ({
  isVisible,
  hideModal,
  onSearchSubmit = (_query: string) => '',
}: Props) => {
  const dispatch = useDispatch();
  const { navigate } = useRouting();
  const intl = useIntl();
  const formatDate = useFormatDate();
  const hasSearchV3 = useFlag(FlagType.SearchV3);
  const hasDeprecatePilotSearch = useFlag(FlagType.DeprecatePilotSearch);
  const { data: sections } =
    api.endpoints.getFilterSections.useQuery(undefined);

  const { data: searchFilters, isFetching: isFetchingFilters } = api.endpoints[
    hasDeprecatePilotSearch ? 'getFiltersV2' : 'getFilters'
  ].useQuery({ type: 'document' });

  const { recentSearches, isLoadingRecentSearches } = useRecentSearches(
    hasDeprecatePilotSearch ? 'EVISEARCH_SEARCH' : 'SEARCH',
  );

  const [isSyntaxPopupOpen, setIsSyntaxPopupOpen] = useState(false);
  const [booleanQuery, setBooleanQuery] = useState('');
  const [validationError, setValidationError] = useState(false);

  const {
    getPinnedFilters,
    result: {
      data: userPreferencesDefaultFilters,
      isFetching: isFetchingPinnedFilters,
      isSuccess: isSuccessPinnedFilters,
    },
  } = usePinnedFiltersDeprecated(sections?.fields);

  const isLoadingPinnedFilters =
    !isSuccessPinnedFilters || isFetchingPinnedFilters;

  const hasNewPins = isLoadingPinnedFilters || userPreferencesDefaultFilters;
  const defaultFilters = hasNewPins
    ? userPreferencesDefaultFilters
    : sections?.defaultFilters
      ? [...sections?.defaultFilters, ...['folder', 'clause'].map(toFilter)]
      : sections?.defaultFilters;

  const [getQuickSearchRecords, getQuickSearchRecordsResponse] =
    api.endpoints.getQuickSearchRecords.useLazyQuery();
  const {
    data: quickSearchRecordsResponse,
    isLoading: isFetchingQuickSearchRecords,
  } = getQuickSearchRecordsResponse;

  const [quickSearchDocs, quickSearchDocumentsResponse] =
    api.endpoints.quickSearchDocuments.useMutation();
  const {
    data: quickSearchDocumentsResult,
    isError: isQuickSearchMutationError,
    isLoading: isQuickSearchMutationLoading,
  } = quickSearchDocumentsResponse;

  const [runSearch, searchResult] = api.endpoints.search.useLazyQuery();
  const {
    data: searchResponse,
    isError: isSearchError,
    isFetching: isSearchLoading,
  } = searchResult;

  const isSearchingDocs = hasDeprecatePilotSearch
    ? isSearchLoading
    : isQuickSearchMutationLoading;
  const isRunningQuickSearchError = hasDeprecatePilotSearch
    ? isSearchError
    : isQuickSearchMutationError;

  const totalCount = hasDeprecatePilotSearch
    ? (searchResponse?.meta?.total ?? 0)
    : (quickSearchDocumentsResult?.documents?.count ?? 0);

  const { data: getFiltersResponse } =
    api.endpoints.getFilterSections.useQuery(undefined);
  const fields = getFiltersResponse?.fields;

  useEffect(() => {
    if (!booleanQuery) {
      getQuickSearchRecords(undefined);
    }
  }, [booleanQuery]);

  useEffect(() => {
    getPinnedFilters('SEARCH');
  }, []);

  const resetSearchQueries = () => {
    dispatch(search.actions.reset());
    dispatch(slices.searchV3.actions.reset());
  };

  const runQuickSearch = useCallback(
    debounce((value) => {
      trackSegment('quickSearchSearchKeywords', {
        name: value,
      });
      if (hasDeprecatePilotSearch) {
        runSearch({
          query: buildQuery({
            booleanQuery: value,
            filters: [],
            searchFilters: searchFilters ?? {},
          }),
          pageSize: QUICK_SEARCH_DOCUMENT_COUNT,
          page: 1,
          sortBy: DEFAULT_SORT_BY.document,
          type: 'document',
          filters: ['document_handler_id_Document', 'folder_Document'],
        });
      } else {
        quickSearchDocs({
          query: { booleanQuery: value },
          size: QUICK_SEARCH_DOCUMENT_COUNT,
        });
      }
    }, SEARCH_DEBOUNCE_MS),
    [quickSearchDocs, runSearch, hasDeprecatePilotSearch, searchFilters],
  );

  const handleQuickSearch = (value: string) => {
    setBooleanQuery(value);
    if (!validationError) {
      runQuickSearch(value);
    }
  };

  const handleSearchBarSubmit = () => {
    trackSegment('quickSearchSelectAdvancedSearch', {
      name: totalCount ? 'With Search Results' : 'No Search Results',
    });
    resetSearchQueries();
    onSearchSubmit(booleanQuery);
    hideModal();
  };

  const handleAdvancedSearchLinkClick = () => {
    hideModal();
    dispatch(search.actions.setPage(1));
    navigate('/search');
  };

  const handleSearchWithQuery = (searchRecord: Option) => {
    trackSegment('quickSearchSelectRecentSearch');
    dispatch(search.actions.clearUnsupportedQuery());
    const searchQuery = searchRecord?.value as PilotSearchQuery;
    const isComplexSearch = checkSearchQueryHasUnsupportedEntity(searchQuery);
    const isComplexV3Query = !!(searchQuery as unknown as SearchQuery).find(
      (q) => q.type === 'section',
    );
    dispatch(search.actions.clearBooleanQuery());
    dispatch(search.actions.clearQuery());
    dispatch(search.actions.clearUnsupportedQuery());
    dispatch(search.actions.setFilters([]));
    dispatch(slices.searchV3.actions.reset());

    if (isComplexSearch || isComplexV3Query) {
      dispatch(slices.searchV3.actions.setQueryBuilder(searchQuery));
      dispatch(search.actions.setUnsupportedQuery(searchQuery));
    } else {
      const booleanSearchItem = searchQuery.find(
        (q) => q.entity === QueryEntityType.BoolTextSearch,
      ) as BooleanTextSearchEntity;

      const bq = booleanSearchItem ? booleanSearchItem.query : '';
      const supportedFieldEntities = searchQuery.filter((f) =>
        testIsSupportedFilterType(f),
      );
      const filters = toFilters(supportedFieldEntities) as FiltersType;
      const v3Filters = toSearchV3Filters(
        searchQuery as unknown as SearchQuery,
        searchFilters ?? {},
      );

      const filterSet = [...defaultFilters!, ...filters] as FiltersType;

      dispatch(
        search.actions.setQuery({
          booleanQuery: bq,
          filters: filterSet.filter(isActiveFilter),
          fields,
        }),
      );
      dispatch(search.actions.setBooleanQuery(bq));
      dispatch(search.actions.setFilters(filters));
      dispatch(slices.searchV3.actions.setSelectedFilters(v3Filters));
    }
    handleAdvancedSearchLinkClick();
  };

  const onRecentViewedDocClick = (docOption: Option) => {
    trackSegment('quickSearchSelectRecentDocument', {
      name: docOption.value,
    });
    handleDocumentClick(docOption);
  };

  const onQuickSearchDocClick = (docOption: Option) => {
    trackSegment('quickSearchSelectResult', {
      name: docOption.value,
    });
    handleDocumentClick(docOption);
  };

  const handleDocumentClick = (docOption: Option) => {
    hideModal();
    navigate(`/document/${docOption.value}`);
  };

  const renderRecentRecords = () => {
    if (isSearchingDocs || booleanQuery) {
      return <></>;
    }
    return (
      <>
        {renderRecentlyViewedDocs()}
        <Layout direction="column" py={4}>
          <Divider />
        </Layout>
        <>{hasSearchV3 ? renderV3RecentSearches() : renderRecentSearches()}</>
      </>
    );
  };

  const renderDocumentPath = (path: Folder[]) =>
    path.map((f) => f.name).join(' / ');

  const renderRecentlyViewedDocs = () => {
    const recentlyViewedDocs =
      quickSearchRecordsResponse?.recent_document_handlers;

    if (recentlyViewedDocs) {
      const options = recentlyViewedDocs.map((item) => {
        return {
          label: item.document_name,
          value: item.document_handler_id,
          leftIcon: getFileIconNameByFileType(item.file_type),
          description: renderDocumentPath(item.path),
          descriptionTruncateMode: 'center' as 'center',
          pathname: `/document/${item.document_handler_id}`,
        };
      });

      return (
        <>
          <Layout direction="column" mb={4}>
            <Text color="text.secondary" variant="tiny-bold">
              {intl.formatMessage({
                id: 'QuickSearchModal.recentlyViewedDocuments',
                defaultMessage: 'RECENTLY VIEWED DOCUMENTS',
                description: 'Section header for recently viewed documents',
              })}
            </Text>
          </Layout>
          {options.length ? (
            <Menu
              enableMenuBorder={false}
              name="Recently viewed documents"
              options={options}
              trigger={null}
              onSelectOption={onRecentViewedDocClick}
            />
          ) : (
            <Text>
              {intl.formatMessage({
                id: 'QuickSearchModal.docsNoRecentlyViewed',
                defaultMessage: 'No recently viewed documents.',
                description:
                  'Message when there are no recently viewed documents',
              })}
            </Text>
          )}
        </>
      );
    }
  };

  const renderV3RecentSearches = () => {
    return (
      <>
        <Box mb={4}>
          <Text color="text.secondary" variant="tiny-bold">
            {intl.formatMessage({
              id: 'QuickSearchModal.recentAdvancedSearches',
              defaultMessage: 'RECENT ADVANCED SEARCHES',
              description: 'Section header for recent advanced searches',
            })}
          </Text>
        </Box>
        <Layout direction="column">
          {recentSearches.length ? (
            <ul
              aria-label="Recent advanced searches"
              style={{ paddingLeft: '0' }}
              role="menu"
            >
              {recentSearches.map((recentSearch, index) => (
                <Box
                  key={`recent_search_${index}`}
                  as="li"
                  role="menuitem"
                  style={{
                    listStyle: 'none',
                  }}
                  p={2}
                  onClick={() =>
                    handleSearchWithQuery({
                      value: recentSearch.query,
                      label: '',
                    })
                  }
                >
                  <ReadOnlyComplexQuery
                    queryBuilder={recentSearch.query ?? []}
                    searchFilters={searchFilters ?? {}}
                  />
                </Box>
              ))}
            </ul>
          ) : (
            <Text>
              {intl.formatMessage({
                id: 'QuickSearchModal.noRecentSearches',
                defaultMessage: 'No recent searches.',
                description: 'Message when there are no recent searches',
              })}
            </Text>
          )}
        </Layout>
      </>
    );
  };

  const renderRecentSearches = () => {
    const recentSearches = quickSearchRecordsResponse?.recent_searches;
    if (recentSearches) {
      const options = recentSearches.map((record) => {
        const booleanSearchRecord = record.query.find(
          (item) => item.entity === QueryEntityType.BoolTextSearch,
        ) as BooleanTextSearchEntity;
        const tags = generateTextStringForFilterTags(
          record as RecentSearches,
          formatDate,
        );

        return {
          label: booleanSearchRecord?.query || '',
          value: record?.query as PilotSearchQuery,
          leftIcon: 'expiring' as const,
          tags,
        };
      });
      return (
        <>
          <Box mb={4}>
            <Text color="text.secondary" variant="tiny-bold">
              {intl.formatMessage({
                id: 'QuickSearchModal.recentAdvancedSearches',
                defaultMessage: 'RECENT ADVANCED SEARCHES',
                description: 'Section header for recent advanced searches',
              })}
            </Text>
          </Box>
          {options.length ? (
            <Menu
              enableMenuBorder={false}
              name={intl.formatMessage({
                id: 'QuickSearchModal.recentAdvancedSearchesMenu',
                defaultMessage: 'Recent advanced searches',
                description: 'Menu name for recent advanced searches',
              })}
              options={options}
              trigger={null}
              onSelectOption={handleSearchWithQuery}
            />
          ) : (
            <Text>
              {intl.formatMessage({
                id: 'QuickSearchModal.noRecentSearches',
                defaultMessage: 'No recent searches.',
                description: 'Message when there are no recent searches',
              })}
            </Text>
          )}
        </>
      );
    }
  };

  const renderMessage = (title: string, content: JSX.Element) => {
    return (
      <Layout
        direction="column"
        alignSelf="center"
        align="center"
        w="250px"
        mt={8}
      >
        <Box>
          <Text variant="subtitle">{title}</Text>
        </Box>
        {content}
      </Layout>
    );
  };

  const renderQuickSearchDocuments = () => {
    const options = hasDeprecatePilotSearch
      ? searchResponse?.results?.map((item) => {
          const handler =
            item.selected_field_values?.document_handler_id_Document
              ?.value_structured?.[0];
          const folders =
            item.selected_field_values?.folder_Document?.value_structured;
          return {
            label: handler?.name ?? '',
            value: handler?.id ?? item.id,
            leftIcon: getFileIconNameByFileType(handler?.file_type ?? ''),
            description:
              folders?.map((f: { name: string }) => f.name).join(' / ') ?? '',
            descriptionTruncateMode: 'center' as 'center',
            pathname: `/document/${handler?.id ?? item.id}`,
          };
        })
      : quickSearchDocumentsResult?.documents?.results?.map((item) => ({
          label: item.document_name,
          value: item.document_id,
          leftIcon: getFileIconNameByFileType(item.file_type),
          description: renderDocumentPath(item.path),
          descriptionTruncateMode: 'center' as 'center',
          pathname: `/document/${item.document_id}`,
        }));

    const onClickNoResultsGoToAdvancedSearch = () => {
      trackSegment('quickSearchSelectAdvancedSearch', {
        name: 'No Search Results',
      });
      handleAdvancedSearchLinkClick();
    };

    if (!booleanQuery || (isSearchingDocs && booleanQuery)) {
      return <></>;
    } else if (
      booleanQuery &&
      !validationError &&
      options &&
      options.length === 0
    ) {
      const message = (
        <Text>
          {intl.formatMessage(
            {
              id: 'QuickSearchModal.noResultsMessage',
              defaultMessage:
                'Try searching for something else or go to {searchLink} to use filters',
              description:
                'Message shown when no quick search results are found, with link to advanced search',
            },
            {
              searchLink: (
                <Link onClick={onClickNoResultsGoToAdvancedSearch}>
                  {intl.formatMessage({
                    id: 'QuickSearchModal.searchLink',
                    defaultMessage: 'Search',
                    description: 'Link text for advanced search',
                  })}
                </Link>
              ),
            },
          )}
        </Text>
      );
      return renderMessage(
        intl.formatMessage({
          id: 'QuickSearchModal.noResultsTitle',
          defaultMessage: 'No results',
          description: 'Title for no results message in quick search',
        }),
        message,
      );
    } else if (options?.length) {
      return (
        <Menu
          enableMenuBorder={false}
          name={intl.formatMessage({
            id: 'QuickSearchModal.quickSearchDocumentsMenuName',
            defaultMessage: 'Search results',
            description: 'Name for quick search documents menu',
          })}
          options={options}
          search={booleanQuery}
          trigger={null}
          onSelectOption={onQuickSearchDocClick}
        />
      );
    } else if (isRunningQuickSearchError && !validationError) {
      const message = (
        <Text>
          {intl.formatMessage({
            id: 'QuickSearchModal.errorWhileSearchingDocuments',
            defaultMessage: 'while searching for documents. Please try again.',
            description: 'Error message when quick search fails for documents',
          })}
        </Text>
      );
      return renderMessage(
        intl.formatMessage({
          id: 'QuickSearchModal.errorOccurredTitle',
          defaultMessage: 'An error occurred',
          description: 'Title for error message when quick search fails',
        }),
        message,
      );
    } else {
      return <></>;
    }
  };

  const onOperatorsClick = () => {
    trackSegment('quickSearchSelectOperators');
    setIsSyntaxPopupOpen(true);
  };

  const renderValidationErrorMessage = () => {
    if (validationError) {
      const message = (
        <Text>
          {intl.formatMessage(
            {
              id: 'QuickSearchModal.unsupportedSearchQueryMessage',
              defaultMessage:
                '{searchLink} cheat sheet may help build complex searches',
              description:
                'Message suggesting to use the search cheat sheet for complex queries',
            },
            {
              searchLink: (
                <Link onClick={() => setIsSyntaxPopupOpen(true)}>
                  {intl.formatMessage({
                    id: 'QuickSearchModal.searchCheatSheetLink',
                    defaultMessage: 'Search',
                    description: 'Link text for search cheat sheet',
                  })}
                </Link>
              ),
            },
          )}
        </Text>
      );
      return renderMessage(
        intl.formatMessage({
          id: 'QuickSearchModal.unsupportedSearchQueryTitle',
          defaultMessage: 'Unsupported search query',
          description: 'Title for unsupported search query message',
        }),
        message,
      );
    } else {
      return null;
    }
  };

  // TODO: design to recomend formal usage with eds.Header patterns
  const pageHeader = (
    <>
      <Layout
        borderBottom="border.divider"
        direction="column"
        flex="none"
        px={6}
        pt={4}
        spacing={4}
      >
        <Layout justify="space-between" spacing={6}>
          <Button
            variant="action"
            text={intl.formatMessage({
              id: 'QuickSearchModal.operatorsButton',
              defaultMessage: 'Operators',
              description: 'Button text for opening operators popup',
            })}
            onClick={onOperatorsClick}
          />
          {totalCount && booleanQuery ? (
            <Link onClick={handleSearchBarSubmit}>
              {intl.formatMessage(
                {
                  id: 'QuickSearchModal.seeAllMatchingDocuments',
                  defaultMessage:
                    'See all {count, plural, one {# matching document} other {# matching documents}} in Search',
                  description:
                    'Link text to see all matching search results. Example: count = 25',
                },
                { count: totalCount },
              )}
            </Link>
          ) : (
            <Link
              onClick={() => {
                trackSegment('quickSearchSelectAdvancedSearch', {
                  name: booleanQuery ? 'No Search Results' : 'No Search Run',
                });
                resetSearchQueries();
                handleAdvancedSearchLinkClick();
              }}
            >
              {intl.formatMessage({
                id: 'QuickSearchModal.searchWithFilters',
                defaultMessage: 'Search',
                description: 'Link text to search with filters',
              })}
            </Link>
          )}
        </Layout>
        {hasDeprecatePilotSearch ? (
          <BooleanSearchBar
            isSubmitting={false}
            value={booleanQuery}
            onChange={handleQuickSearch}
            onSubmit={handleSearchBarSubmit}
          />
        ) : (
          <BaseBooleanSearchBar
            includeOperatorButton={false}
            isDisabled={false}
            isSubmitting={false}
            value={booleanQuery}
            onChange={handleQuickSearch}
            onSubmit={handleSearchBarSubmit}
            onValidationError={setValidationError}
          />
        )}
      </Layout>
    </>
  );

  return (
    <PageOverlay
      disableHideOnEscape={false}
      header={pageHeader}
      isStretched
      isVisible={isVisible}
      width="l"
      onHide={hideModal}
    >
      <ContentContainer
        loadingContent={{
          isLoading:
            isFetchingQuickSearchRecords ||
            isFetchingFilters ||
            isLoadingRecentSearches,
        }}
      >
        {renderRecentRecords()}
      </ContentContainer>
      <ContentContainer loadingContent={{ isLoading: isSearchingDocs }}>
        {renderQuickSearchDocuments()}
      </ContentContainer>
      {renderValidationErrorMessage()}
      {isSyntaxPopupOpen && (
        <EcBooleanTextSearchPopup
          handleOnCloseClick={() => setIsSyntaxPopupOpen(false)}
        />
      )}
    </PageOverlay>
  );
};

export default QuickSearchModal;
